/**
 * Created by renlaydel on 20/11/2017.
 */
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import { PurchaseReceiptForm } from '../../../../constants/constant_form_names';
import { BATCH, SERIALIZED } from '../../../../constants/constant_item_types';
import {
  CLOSED,
  CLOSED_ABANDONED,
  ERROR,
  PROCESSING,
  OPEN,
} from '../../../../constants/constant_transaction_status';

const selector = formValueSelector(PurchaseReceiptForm);

function getFormItemLines(state) {
  return selector(state, 'lines') || [];
}

function getFormLineByIndex(state, index) {
  return selector(state, `lines[${index}]`) || {};
}

function getFormStatus(state) {
  return selector(state, 'status') || {};
}

function getFormRunStatus(state) {
  return selector(state, 'run_status');
}

function getFormPurchaseOrderReference(state) {
  return selector(state, 'purchase_order');
}

function getFormCurrency(state) {
  return selector(state, 'currency') || {};
}

function getFormSubsidiary(state) {
  const subsidiary = selector(state, 'subsidiary') || {};
  return subsidiary;
}

function getFormLocation(state) {
  return selector(state, 'location_id');
}

function getFormLocationCurrency(state) {
  return selector(state, 'location_currency') || {};
}

function getFormSupplierCurrency(state) {
  return selector(state, 'supplier_currency') || {};
}

function getFormDispatchedDate(state) {
  return selector(state, 'dispatched_date');
}

function getFormId(state) {
  const { _id, id } = selector(state, '_id', 'id');
  return _id || id;
}

function getPropViewMode(state, viewMode) {
  return viewMode;
}

function getFormReferenceId(state) {
  return selector(state, 'reference_id');
}

function getEnableItemPartQuantityPreference(state) {
  return ((state.preference || {}).settings || {}).enable_item_part_quantity;
}

export const getReferenceId = createSelector(
  [getFormReferenceId],
  (referenceId) => {
    return referenceId;
  },
);

export const getSubsidiary = createSelector(
  [getFormSubsidiary],
  (subsidiary) => {
    return subsidiary;
  },
);

export const hasOnEditModeLine = createSelector(
  [getFormItemLines],
  (itemLines) => {
    if (!itemLines.length) return false;
    return itemLines.some((itemLine) => {
      return (
        itemLine &&
        !itemLine.quantity &&
        !itemLine.item_id &&
        itemLine.onEditMode
      );
    });
  },
);

export const getEditModeLineIndex = createSelector(
  [getFormItemLines],
  (itemLines) => {
    if (!itemLines.length) return null;
    return itemLines.findIndex((itemLine) => {
      return itemLine && itemLine.onEditMode;
    });
  },
);

export const getValidCurrency = createSelector(
  [getFormLocationCurrency, getFormSupplierCurrency],
  (locationCurrency, supplierCurrency) => {
    const locationCurrencyId = (locationCurrency || {}).id;
    const supplierCurrencyId = (supplierCurrency || {}).id;
    const includedCurrencies = [];

    if (locationCurrencyId) includedCurrencies.push(locationCurrencyId);
    if (supplierCurrencyId) includedCurrencies.push(supplierCurrencyId);

    return uniq(includedCurrencies);
  },
);

export const isClosed = createSelector(
  [getFormStatus],
  (status) => {
    return [CLOSED_ABANDONED, CLOSED].indexOf(status.cid) > -1;
  },
);

export const isOpen = createSelector(
  [getFormStatus],
  (status) => {
    return [OPEN].indexOf(status.cid) > -1;
  },
);

export const hasRunStatus = createSelector(
  [getFormRunStatus],
  (runStatus) => {
    return Boolean(runStatus);
  },
);

export const viewModeForm = createSelector(
  [getFormStatus, hasRunStatus, getPropViewMode],
  (status, hasRunStatus, viewMode) => {
    return (
      [CLOSED_ABANDONED, CLOSED, ERROR, PROCESSING].indexOf(status.cid) > -1 ||
      hasRunStatus ||
      viewMode
    );
  },
);

export const hasBatchSerializedItems = createSelector(
  [getFormItemLines],
  (itemLines) => {
    if (!itemLines.length) return false;
    return itemLines.some((itemLine) => {
      if (!itemLine) return false;
      const itemType = itemLine.item_type;
      return itemType === BATCH || itemType === SERIALIZED;
    });
  },
);

export const getLocationCurrencyCode = createSelector(
  [getFormLocationCurrency],
  (locationCurrency) => {
    return (locationCurrency || {}).code;
  },
);

export const getLocationCurrencyId = createSelector(
  [getFormLocationCurrency],
  (locationCurrency) => {
    return (locationCurrency || {}).id;
  },
);

export const allowGenerateReturn = createSelector(
  [getFormStatus],
  (status) => {
    return [CLOSED].indexOf(status.cid) > -1;
  },
);

export const getCurrencyCode = createSelector(
  [getFormCurrency],
  (currency) => {
    return currency.code;
  },
);

export const getCurrencySymbol = createSelector(
  [getFormCurrency],
  (currency) => {
    return currency.symbol;
  },
);

export const getUnfilledLineItemIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.findIndex((line) => {
      return line && !line.quantity && !line.item_id;
    });
  },
);

export const hasPurchaseOrder = createSelector(
  [getFormPurchaseOrderReference],
  (purchaseOrder) => {
    return Boolean((purchaseOrder || {}).id);
  },
);

export const getUnfilledLineReferenceIdIndex = createSelector(
  [getFormLineByIndex],
  (itemLine) => {
    return (itemLine.reference_ids || []).findIndex((referenceId) => {
      return referenceId && !referenceId.reference;
    });
  },
);

export const getEditModeLineReferenceIdIndex = createSelector(
  [getFormLineByIndex],
  (itemLine) => {
    if (!(itemLine.reference_ids || []).length) return null;
    return itemLine.reference_ids.findIndex((itemLineRefId) => {
      return itemLineRefId && itemLineRefId.onRefLineEditMode;
    });
  },
);

export const hasItemWithReturnQty = createSelector(
  [getFormItemLines],
  (itemLines) => {
    if (!itemLines.length) return false;
    return itemLines.some((itemLine) => {
      if (!itemLine) return false;
      const returnedQty = Number(itemLine.returned_quantity) || 0;
      return returnedQty > 0;
    });
  },
);

export const getDispatchedDate = createSelector(
  [getFormDispatchedDate],
  (dispatchedDate) => dispatchedDate,
);

export const isExistingReceipt = createSelector(
  [getFormId],
  (id) => {
    return Boolean(id);
  },
);

export const getLocationId = createSelector(
  [getFormLocation],
  (locationId) => locationId,
);

export const getExcludedItems = createSelector(
  [getFormItemLines, getFormLineByIndex],
  (itemLines, line) => {
    const selectedItemIds = itemLines.map((itemLine) => itemLine.item_id);
    const { item_id } = line;
    const excludedItemIds = without(
      selectedItemIds,
      undefined,
      null,
      '',
      item_id,
    );
    return excludedItemIds;
  },
);

export const isLineBatchSerializedItem = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { item_type } = line;
    return [BATCH, SERIALIZED].indexOf(item_type) > -1;
  },
);

export const getFormLines = createSelector(
  [getFormItemLines],
  (lines) => lines,
);

export const hasLines = createSelector(
  [getFormItemLines],
  (lines) => Boolean(lines.length),
);

export const getLineItemUom = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { item_uom } = line;
    return item_uom;
  },
);

export const getLineOnEditMode = createSelector(
  [getFormLineByIndex],
  (line) => line.onEditMode,
);

export const getLineId = createSelector(
  [getFormLineByIndex],
  (line) => line.line_id,
);

export const hasLineReturnedQty = createSelector(
  [getFormLineByIndex],
  (line) => Number(line.returned_quantity) > 0,
);

export const getLineItemName = createSelector(
  [getFormLineByIndex],
  (line) => (line.item || {}).name,
);

export const getLineIsFetchingItemLocation = createSelector(
  [getFormLineByIndex],
  (line) => Boolean(line.isFetchingItemLocation),
);

export const getLineIsFetchingTaxGroup = createSelector(
  [getFormLineByIndex],
  (line) => Boolean(line.isFetchingTaxGroup),
);

export const getLineTax = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { total, total_ex_tax } = line;
    return (total || 0) - (total_ex_tax || 0);
  },
);

export const hasLineNextStatus = createSelector(
  [getFormLineByIndex],
  (line) => Boolean(line.next_status_id),
);

export const isPartQuantity = createSelector(
  [getFormLineByIndex, getEnableItemPartQuantityPreference],
  (line, enableItemPartQuantity) => {
    const { item } = line;
    return enableItemPartQuantity && (item || {}).enable_part_quantity;
  },
);
